/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {PageLink, PageTitle} from '../../../_metronic/layout/core'
import {KTSVG} from '../../../_metronic/helpers'
import {useToast} from '../components/Toats'
import {Loading} from '../components/Loadings/loading'
import {LoadingSpan} from '../components/Loadings/loadingSpan'
import {
  downloadDocumentation,
  downloadOpenApi,
  DocumentationFile,
  DocumentationList,
  getDocumentationContent,
  getDocumentations,
} from '../../services/documentationService'

/**
 * Documentações técnicas do sistema (só ADMIN): manuais em Markdown, contrato OpenAPI
 * da API pública e coleção do Insomnia para testes.
 */

const breadcrumbs: Array<PageLink> = [
  {title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false},
  {title: '', path: '', isSeparator: true, isActive: false},
]

const modalsRoot = document.getElementById('root-modals') || document.body

const formatSize = (size: number | null) => {
  if (size === null) return '-'
  return size < 1024 ? `${size} B` : `${(size / 1024).toFixed(0)} KB`
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR', {dateStyle: 'short', timeStyle: 'short'}) : '-'

const TYPE_INFO: Record<DocumentationFile['type'], {label: string; icon: string; color: string}> = {
  markdown: {label: 'Manual (.md)', icon: '/media/icons/duotune/files/fil003.svg', color: 'primary'},
  insomnia: {label: 'Insomnia (.json)', icon: '/media/icons/duotune/coding/cod002.svg', color: 'info'},
}

const DocumentationsPage: FC = () => {
  const {toggleToast} = useToast()
  const [list, setList] = useState<DocumentationList | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [viewing, setViewing] = useState<{file: DocumentationFile; content: string} | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setList(await getDocumentations())
    } catch (error: any) {
      toggleToast(error?.response?.data?.message || 'Erro ao buscar as documentações!', 'error')
    } finally {
      setLoading(false)
    }
  }

  const download = async (file: DocumentationFile) => {
    try {
      setDownloading(file.key)
      await downloadDocumentation(file)
    } catch (error) {
      toggleToast('Erro ao baixar o arquivo!', 'error')
    } finally {
      setDownloading(null)
    }
  }

  const view = async (file: DocumentationFile) => {
    try {
      setViewing({file, content: await getDocumentationContent(file.key)})
    } catch (error) {
      toggleToast('Erro ao abrir o arquivo!', 'error')
    }
  }

  const downloadSpec = async () => {
    if (!list) return
    try {
      setDownloading('openapi')
      await downloadOpenApi(list.openapi.openapi_url)
    } catch (error) {
      toggleToast('Erro ao baixar o openapi.json!', 'error')
    } finally {
      setDownloading(null)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className='card mb-8'>
        <div className='card-body d-flex align-items-start gap-6 py-8'>
          <div className='symbol symbol-60px flex-shrink-0'>
            <span className='symbol-label bg-light-primary'>
              <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-2x svg-icon-primary' />
            </span>
          </div>
          <div>
            <h3 className='fw-bold text-gray-900 mb-2'>Documentações técnicas</h3>
            <div className='text-gray-600 fs-6'>
              Material de referência para desenvolvimento, implantação e integrações do Solar Vision:
              manuais das funcionalidades, o contrato da API pública usada pela integração com a Nuklie
              e a coleção do Insomnia para testar essa API. Disponível só para administradores.
              Os usuários das empresas acessam a documentação da API pela tela Integração via API.
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className='d-flex justify-content-center py-15'><Loading myClass='spinner-border' /></div>
      ) : !list ? (
        <div className='text-center text-muted py-15'>Não foi possível carregar as documentações.</div>
      ) : (
        <div className='row g-6'>
          <div className='col-md-6 col-xxl-3'>
            <div className='card h-100'>
              <div className='card-body d-flex flex-column'>
                <div className='d-flex align-items-center mb-5'>
                  <div className='symbol symbol-45px me-4'>
                    <span className='symbol-label bg-light-success'>
                      <KTSVG path='/media/icons/duotune/coding/cod007.svg' className='svg-icon-2x svg-icon-success' />
                    </span>
                  </div>
                  <span className='badge badge-light-success fw-bold'>OpenAPI 3.0</span>
                </div>
                <span className='fw-bold fs-5 text-gray-900 mb-2'>{list.openapi.title}</span>
                <span className='text-gray-600 fs-7 mb-6 flex-grow-1'>{list.openapi.description}</span>
                <span className='text-muted fs-8 mb-4 text-break font-monospace'>{list.openapi.openapi_url}</span>
                <div className='d-flex gap-2'>
                  <a href={list.openapi.docs_url} target='_blank' rel='noreferrer' className='btn btn-sm btn-primary flex-grow-1'>
                    Abrir documentação
                  </a>
                  <button type='button' className='btn btn-sm btn-light-primary' onClick={downloadSpec} disabled={downloading === 'openapi'}>
                    {downloading === 'openapi' ? <LoadingSpan style={{width: '1rem', height: '1rem'}} /> : 'Baixar .json'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {list.files.map((file) => {
            const info = TYPE_INFO[file.type]
            return (
              <div key={file.key} className='col-md-6 col-xxl-3'>
                <div className='card h-100'>
                  <div className='card-body d-flex flex-column'>
                    <div className='d-flex align-items-center mb-5'>
                      <div className='symbol symbol-45px me-4'>
                        <span className={`symbol-label bg-light-${info.color}`}>
                          <KTSVG path={info.icon} className={`svg-icon-2x svg-icon-${info.color}`} />
                        </span>
                      </div>
                      <span className={`badge badge-light-${info.color} fw-bold`}>{info.label}</span>
                    </div>
                    <span className='fw-bold fs-5 text-gray-900 mb-2'>{file.title}</span>
                    <span className='text-gray-600 fs-7 mb-6 flex-grow-1'>{file.description}</span>
                    <span className='text-muted fs-8 mb-4'>
                      {file.filename} · {formatSize(file.size)} · atualizado em {formatDate(file.updated_at)}
                    </span>
                    {file.available ? (
                      <div className='d-flex gap-2'>
                        {file.type === 'markdown' && (
                          <button type='button' className='btn btn-sm btn-primary flex-grow-1' onClick={() => view(file)}>
                            Visualizar
                          </button>
                        )}
                        <button type='button' className={`btn btn-sm ${file.type === 'markdown' ? 'btn-light-primary' : 'btn-primary flex-grow-1'}`}
                          onClick={() => download(file)} disabled={downloading === file.key}>
                          {downloading === file.key ? <LoadingSpan style={{width: '1rem', height: '1rem'}} /> : 'Baixar'}
                        </button>
                      </div>
                    ) : (
                      <span className='badge badge-light-danger'>Arquivo ausente no servidor</span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {createPortal(
        <Modal dialogClassName='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-1000px' show={!!viewing} onHide={() => setViewing(null)}>
          <div className='modal-header'>
            <h3 className='fw-bold m-0'>{viewing?.file.title}</h3>
            <button type='button' className='btn btn-sm btn-icon btn-light' onClick={() => setViewing(null)}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </div>
          <div className='modal-body'>
            <pre className='fs-7 text-gray-800 mb-0' style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace'}}>
              {viewing?.content}
            </pre>
          </div>
          <div className='modal-footer'>
            {viewing && (
              <button type='button' className='btn btn-light-primary' onClick={() => download(viewing.file)}>Baixar</button>
            )}
            <button type='button' className='btn btn-primary' onClick={() => setViewing(null)}>Fechar</button>
          </div>
        </Modal>,
        modalsRoot
      )}
    </>
  )
}

const DocumentationsWrapper: FC = () => (
  <>
    <PageTitle breadcrumbs={breadcrumbs}>Documentações</PageTitle>
    <DocumentationsPage />
  </>
)

export {DocumentationsWrapper}
